/**
 * feat-205 — reading the coverage envelope, in one place.
 *
 * TRAP-2 is that a hidden row and an empty queue are two different zeros, and after
 * Part B there are three of them: nothing was ever in scope · everything in scope was
 * suppressed as already-covered · the request failed. The queue looks equally empty in
 * all three, and only one of them is good news.
 *
 * ADR 0001 §4.1 makes them decidable **from the response fields, not from
 * `items.length`** — and that distinction is the whole point: `items` is a PAGE, and a
 * page is empty for reasons that have nothing to do with the population. These helpers
 * exist so the branch is written once and asserted once, rather than re-derived at
 * each render site with a slightly different idea of what zero means.
 */
import { RejectionCoverage, RejectionTypeCount } from '../../../services/scopedReadsService';

/**
 * Why the queue has no rows to show.
 *
 * `unknown` is a first-class answer, not a fallback: before Part B ships the API sends
 * no coverage object at all, and claiming "everything was covered" — or "nothing was
 * covered" — from its absence would be inventing a measurement. The UI must say the
 * plain thing ("nothing to review") in that case and nothing about suppression.
 *
 * `unreconciled` is the response contradicting itself: a non-empty reviewable
 * population that returned no first page. It is not a state the backend should ever
 * produce, which is exactly why it needs a name — rendering it as "nothing here" would
 * hide a broken contract behind the most reassuring copy on the screen, which is the
 * failure shape this whole feature exists to end.
 */
export type QueueEmptyKind = 'unknown' | 'nothing_in_scope' | 'all_suppressed' | 'unreconciled';

/**
 * Decide WHY the queue is empty. Call only when there are no items to show; `offset`
 * defaults to 0 because "no rows on page 3" says nothing about the population.
 */
export const deriveEmptyKind = (
  coverage: RejectionCoverage | null | undefined,
  offset = 0,
): QueueEmptyKind => {
  // Absent ≠ zero. Branch on undefined BEFORE branching on any number.
  if (coverage == null) return 'unknown';
  // `applied: false` is the backend telling us it did not run the predicate. Its
  // numbers are then not about suppression, so neither is our copy.
  if (!coverage.applied) return 'unknown';
  if (coverage.total_in_scope === 0) return 'nothing_in_scope';
  if (coverage.reviewable_total === 0) return 'all_suppressed';
  if (offset > 0) return 'unknown';
  return 'unreconciled';
};

/**
 * AC12 — `shown + suppressed` must reconcile to the total **from the response alone**.
 *
 * Checked on the client not because the client owns the arithmetic — it does not, and
 * it must never derive a missing field by subtraction — but because a mismatch means
 * the two halves of the response were computed over different populations, and that
 * failure is invisible unless something asserts it. `reviewable_total` includes
 * `null_span_reviewable`, so the subset is deliberately absent from this sum.
 *
 * feat-206 D11 adds a SECOND labelled subset, `suppressed_zero_length`, and it belongs
 * to the other side: it is counted INSIDE `suppressed_covered`, so it is absent from
 * this sum for the same reason. Adding either subset would break a correct response.
 */
export const coverageReconciles = (coverage: RejectionCoverage): boolean =>
  coverage.reviewable_total + coverage.suppressed_covered === coverage.total_in_scope;

/**
 * AC9, per chip: `shown + suppressed === total`. Returns `null` when the type-count
 * row predates Part B — an unanswerable question, which is not the same as a `false`.
 */
export const chipReconciles = (row: RejectionTypeCount): boolean | null => {
  if (row.reviewable_rejections == null || row.covered_suppressed == null) return null;
  return row.reviewable_rejections + row.covered_suppressed === row.rejections;
};

/** The chip headline: the REVIEWABLE count once we have one, the total until then. */
export const chipHeadlineCount = (row: RejectionTypeCount): number =>
  row.reviewable_rejections ?? row.rejections;

/**
 * feat-205 AC13 — read a count out of `RecallMetricsBlock.counts`, which is
 * `Record<string, number>`: an OPEN MAP with **no type enforcement on any key name.**
 *
 * Rename a key server-side, drop one, or change what one means, and `tsc` stays clean
 * because the map's index signature says every string is a `number`. Reading
 * `counts.covered` directly therefore yields `undefined` while TypeScript insists it is
 * a `number` — which becomes `NaN` in arithmetic and the literal string `"undefined"`
 * in a template. Neither throws, and neither looks like a bug in a screenshot.
 *
 * feat-205-qa established the sharper version of this: **the gap is inert precisely
 * until someone writes the first consumer, and AC13 is that consumer.** So the first
 * consumer is the right place to put the guard.
 *
 * **`undefined` HAS EXACTLY ONE CAUSE: the key is not there any more.** Verified at the
 * emitter, 2026-08-03, against `restaurant-backend` at that date — `scoped_reads.py:4515`
 * builds `counts` as a flat dict literal with **all eighteen keys unconditional** (no
 * conditional insertion, no spread, no `if`); values come from `int(r.get(...) or 0)` at
 * `:4500`; and the by-type aggregate at `:4719` initialises every `covered_*` key to `0`
 * before summing. **A zero count is emitted as `0`. A key is never omitted because its
 * value is zero.**
 *
 * That is a measurement with a date, not a property of the API — it is written down
 * precisely so that **if someone later makes a key conditional, this paragraph is the
 * thing that goes false and gets caught**, rather than the rendering quietly changing
 * meaning. If a key ever can be legitimately absent-when-zero, `—` becomes the wrong
 * glyph for it and this function needs the two cases separated.
 *
 * **Returns `null` for a missing key so the caller renders `—`, never `0`.** Three
 * reasons, in order of weight:
 *  1. A row of dashes is a SYMPTOM someone reports. `0` and `NaN` are not.
 *  2. `0` asserts a measurement we do not have — the same rule this screen already
 *     applies to a failed type-count fetch, where "0" would claim the queue is empty
 *     when the truth is that we failed to look.
 *  3. It degrades ONE row rather than throwing and blanking a panel whose other
 *     numbers are fine.
 *
 * **The only real guard on these key names lives on the backend**, which pins all
 * eighteen by literal name in a test. Nothing on this side can check them; this
 * function makes their absence visible, which is a different and lesser thing.
 */
/**
 * feat-206 D-a — the plain-language gloss for a coverage rule, DERIVED from the token
 * family and never hardcoded beside it.
 *
 * A raw wire token is not, on its own, something to show a human: an evaluator who does
 * not already know what an epsilon is learns nothing from `epsilon_0.5s`. But the fix
 * cannot be an English sentence typed next to the token, because that reproduces the
 * failure this file already records one layer over — the wire said `half_open` while the
 * epsilon was what ran, and the panel would have presented the superseded rule as the
 * audited one. A hardcoded sentence drifts the same way and nothing compares them.
 *
 * So: keyed on the family prefix, and **`null` for a family this build has never heard
 * of**. Describing `frobnicate_v9` as "shares a transcript part" would invent a
 * mechanism, which is strictly worse than the bare token — in that state the
 * unrecognised-rule banner is the only copy that renders.
 *
 * `null` is also what makes {@link isKnownCoverageRule} definable in terms of this
 * function, so the detector and the gloss **cannot disagree**: a family cannot be added
 * to one and forgotten in the other.
 */
export const describeCoverageRule = (semantics: string | undefined | null): string | null => {
  if (typeof semantics !== 'string') return null;
  // feat-206 D9 — the family prefix is `transcript_part_`, the current value
  // `transcript_part_v1`. Prefix, not equality: see the family-not-value note below.
  //
  // **The wording names the CONTAINMENT, not a "shared part"** (design, 2026-08-09,
  // after the D5 revision). "Shares at least one transcript part" was unambiguous under
  // the retired overlap formula and is ambiguous under half-open containment: it reads
  // equally as *the spans overlap the same part* and *a part begins inside both spans*
  // — which are the two predicates that got confused in the first place. This line is
  // the audit line, so it has to pick one. (The per-row neighbour copy deliberately
  // still says "shares a transcript part": it names the wire field `shares_part` and
  // reports its output rather than defining the rule. The divergence is intended.)
  if (semantics.startsWith('transcript_part_')) {
    return "Covered means some transcript part starts inside both spans — this candidate's, and a retained interaction's on the same shift.";
  }
  // SUPERSEDED 2026-08-09 by `transcript_part_v1`, and deliberately still recognised:
  // during rollout an older backend still emits `epsilon_*`, and dropping it would turn
  // a working deploy into a red banner (spec AC19). The gloss describes it in the
  // present tense because, on a response that carries it, it IS the rule that ran.
  if (semantics.startsWith('epsilon_')) {
    return "Covered means a retained interaction's span overlaps the candidate's by more than the stated threshold.";
  }
  return null;
};

/**
 * feat-205 — does this response's stated rule belong to a family this UI understands?
 *
 * **Matches the FAMILY, not the value** (architect, ADR §4.1a). The emitted string is
 * derived from the rule — `epsilon_0.5s` was derived from the threshold, and
 * `transcript_part_v1` carries a version — so it changes whenever the rule changes,
 * which is a deliberate contract change rather than a regression. A check pinned to the
 * exact literal would fire on every legitimate change, and **a detector that fires on
 * legitimate changes is one that gets deleted.**
 *
 * What it still catches is the thing worth catching: a response naming `half_open`, or
 * any other rule this build has never heard of, while the panel prints that name as the
 * audited rule. The full string is always displayed either way — this only decides
 * whether it is displayed with a warning beside it.
 *
 * **Exact on the emitter, family on the consumer** (product-lead's formulation, and it
 * is the reason the asymmetry is correct rather than merely convenient): the emitter's
 * job is to NOTICE the contract moved — backend pins the exact value, and that red is
 * their prompt to tell us — while the consumer's job is to SURVIVE it moving. feat-206
 * is that contract moving, on purpose, and both tripwires fired as designed.
 *
 * **TWO families are recognised, and the old one is not dropped** (feat-206 AC19). Ship
 * order is not atomic across two repos: during rollout an older backend still emits
 * `epsilon_*`, so removing it would light the banner on a deploy that is working
 * correctly.
 *
 * **Defined through {@link describeCoverageRule} on purpose** (design D-a): a widened
 * detector that forgets to widen the gloss would describe the new rule with the old
 * sentence, which is the drift this feature exists to delete. One list, one place.
 *
 * **Newly added 2026-08-03, and stated plainly because the distinction matters:** before
 * this, the "detector" existed only as a paragraph in a type doc. A comment describing a
 * check is not a check, and it was cited back to me as though it were one.
 */
export const isKnownCoverageRule = (semantics: string | undefined | null): boolean =>
  describeCoverageRule(semantics) !== null;

export const readCount = (
  counts: Record<string, number> | undefined,
  key: string,
): number | null => {
  const value = counts?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};
